/**
 * EHS MIS report API client (/api/reports/ehs-mis).
 *
 * Auth is injected globally by the axios request interceptor in App.js, so
 * these helpers never touch tokens. The backend resolves and authorizes the
 * site scope itself (fail-closed) — passing site_id here is a request, not a
 * grant.
 */
import axios from 'axios';

const base = () => `${process.env.REACT_APP_BACKEND_URL}/api/reports/ehs-mis`;

/**
 * Fetch the EHS MIS grid as JSON.
 * params: { siteId?, fy?, costPerManDay? }
 * Returns { site_id, site_label, fy_label, months, rows, caveats, factor_set, fr_basis }.
 */
export async function fetchEhsMis({ siteId, fy, costPerManDay } = {}, { signal } = {}) {
  const params = { format: 'json' };
  if (siteId) params.site_id = siteId;
  if (fy) params.fy = fy;
  if (costPerManDay !== undefined && costPerManDay !== null && costPerManDay !== '') {
    params.cost_per_man_day = costPerManDay;
  }
  const { data } = await axios.get(base(), { params, signal });
  return data;
}

/**
 * Download the report as csv | excel | pdf. Returns { blob, filename }.
 */
export async function downloadEhsMis({ siteId, fy, costPerManDay, format }) {
  const params = { format };
  if (siteId) params.site_id = siteId;
  if (fy) params.fy = fy;
  if (costPerManDay !== undefined && costPerManDay !== null && costPerManDay !== '') {
    params.cost_per_man_day = costPerManDay;
  }
  const resp = await axios.get(base(), { params, responseType: 'blob' });
  const cd = resp.headers['content-disposition'] || '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  const ext = format === 'excel' ? 'xlsx' : format;
  return { blob: resp.data, filename: match ? match[1] : `ehs_mis.${ext}` };
}

/**
 * Save hand-entered values for the rows the platform cannot source.
 * entries: [{ row_key, month_index, value }] — value null clears the cell.
 * The server re-checks the write grant and rejects any row that is not
 * hand-enterable, so this is a request, not an authorization.
 */
export async function saveEhsMisEntries({ siteId, fy, entries }) {
  const { data } = await axios.put(`${base()}/entries`, {
    site_id: siteId || null,
    fy: fy || null,
    entries,
  });
  return data;
}

// ---- Row configuration: custom rows + hidden rows ----

/** Add a site-defined row. Returns { id, row_key }. */
export async function createCustomRow(siteId, row) {
  const { data } = await axios.post(`${base()}/custom-rows`, row, {
    params: siteId ? { site_id: siteId } : {},
  });
  return data;
}

/** Rename or re-configure a site-defined row. Values are untouched. */
export async function updateCustomRow(siteId, rowId, row) {
  const { data } = await axios.put(`${base()}/custom-rows/${rowId}`, row, {
    params: siteId ? { site_id: siteId } : {},
  });
  return data;
}

/** Remove a site-defined row. Values already entered are retained, just unused. */
export async function deleteCustomRow(siteId, rowId) {
  const { data } = await axios.delete(`${base()}/custom-rows/${rowId}`, {
    params: siteId ? { site_id: siteId } : {},
  });
  return data;
}

/**
 * Replace the set of rows this site leaves off the report. The request is the
 * desired end state, so un-hiding a row is simply omitting its key.
 */
export async function setHiddenRows(siteId, rowKeys) {
  const { data } = await axios.put(`${base()}/hidden-rows`, {
    site_id: siteId || null,
    row_keys: rowKeys,
  });
  return data;
}

/** Trigger a browser download for a fetched blob. */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
